<template>
  <app-wrapper>
    <app-header :loan="loan" medium-max-width="max-w-1000">
      <template #navigation>
        <div class="max-w-200">
          <h5 class="font-zen-dots text-white">
            <AppLogo/>
          </h5>
        </div>
      </template>
      <template #top>
        <div class="w-full flex items-center">
          <div class="flex-auto">
            Your desired loan amount
            <h2 class="mb-0 mt-2 md:mt-4 md:text-6xl">{{ loan.amount | money(0) }}</h2>
          </div>
          <div class="flex-initial">
            <i class="fa fa-check-circle text-success text-7xl"/>
          </div>
        </div>
      </template>
    </app-header>
    <app-body medium-max-width="max-w-500" footer>

      <template #title>
        Verify Income
      </template>

      <template #text>
        <!--
          The park's own explanation, resolved server-side by the same class that writes the signup
          upload page's copy. It wins: the blocks below are derived from the Plaid report rather than
          from the park, so they are all false for verdicts no flag covers — which is how a held
          borrower used to read "funded within 24 hours" over a hold. They survive as the fallback
          for the states the resolver does not own (flags true with no park, and its two deliberate
          exclusions).
        -->
        <div v-if="parkMessage">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2">{{ parkMessage }}</p>
          </div>
        </div>
        <div v-else-if="!isPaystubOutdated && !isPaystubInFuture && !isPaystubError && !isPaystubMissingName && !isPaystubMissingAmount">
          Once your income is verified, you will get funded within 24 hours.
        </div>
        <div v-else-if="isPaystubOutdated">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2" v-if="expectedPayDate">
              This paystub is from {{ paystubDate }}. Please upload your paystub for the pay period covering {{ expectedPayDate }}.
            </p>
            <p class="ml-2" v-else>
              This paystub is from {{ paystubDate }}, please provide your most recent paystub.
            </p>
          </div>
        </div>
        <div v-else-if="isPaystubInFuture">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2">The deposit date for this paystub is {{ paystubDate }}.  Please provide your most recent paystub that’s already been deposited</p>
          </div>
        </div>
        <div v-else-if="isPaystubError">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2">We were unable to process your paystub. Please make sure your paystub is not password protected and submit again</p>
          </div>
        </div>
        <div v-else-if="isPaystubMissingName">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2">We were unable to find your name on the paystub. Please upload a paystub that includes your name.</p>
          </div>
        </div>
        <div v-else-if="isPaystubMissingAmount">
          <div class="flex items-center">
            <i class="fa fa-exclamation-triangle text-warning text-2xl"/>
            <p class="ml-2">We were unable to find the pay amount on your paystub. Please upload a clearer paystub that shows your net pay.</p>
          </div>
        </div>
      </template>

      <template #main>
        <div v-if="! showBanking && ! showManual" class="mt-7">

          <div @click.prevent="showBanking = true"
               class="bg-white border-gray-300 cursor-pointer shadow-lg border p-3 rounded-lg flex space-x-3">
            <div class="bg-terciary rounded-md px-3 flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                   class="text-action w-8 h-8">
                <path fill-rule="evenodd"
                      d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z"
                      clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-auto space-y-1 md:py-2">
              <p class="text-lg font-bold">Instant Connection</p>
              <p>Click here to securely verify your income</p>
            </div>
            <div class="flex items-center justify-center">
              <span class="bg-green-100 text-green-500 rounded-lg p-2 mr-4 font-bold">FAST</span>
            </div>
          </div>
        </div>
        <div v-else-if="showBanking && loan.banking_vendor_id === 2" class="mt-7 space-y-7">

          <div v-if="plaidLoading" class="flex justify-center">
            <i class="fa fa-circle-notch fa-spin text-3xl"/>
          </div>

          <div @click.prevent="plaid.open()"
               class="flex cursor-pointer items-center bg-gray-50 shadow rounded-lg md:h-full">

            <div class="flex-auto py-6 px-6">
              <svg class="h-8" viewBox="0 0 126 48" xmlns="http://www.w3.org/2000/svg">
                <g fill="#111" fill-rule="evenodd">
                  <path
                      d="M66.248 16.268c-1.057-.889-2.861-1.333-5.413-1.333h-5.756v17.788h4.304v-5.575h1.928c2.34 0 4.056-.515 5.148-1.546 1.23-1.155 1.849-2.693 1.849-4.613 0-1.991-.687-3.565-2.06-4.721m-5.044 6.855h-1.821V18.96h1.636c1.99 0 2.985.698 2.985 2.094 0 1.378-.934 2.068-2.8 2.068M75.673 14.934h-4.488v17.788h9.69v-4.026h-5.202zM89.668 14.934l-7.05 17.788h4.832l.924-2.586H94.5l.845 2.586h4.886l-7-17.788zm-.053 11.601l1.849-6.08 1.82 6.08z"></path>
                  <path
                      d="M102.473 32.722h4.489V14.934h-4.489zM124.39 18.268a7.376 7.376 0 00-2.14-2.053c-1.355-.854-3.204-1.28-5.545-1.28h-5.914v17.787h6.918c2.5 0 4.506-.817 6.02-2.453 1.514-1.635 2.27-3.805 2.27-6.508 0-2.15-.537-3.981-1.61-5.493m-7.182 10.427h-1.927v-9.734h1.954c1.373 0 2.428.43 3.168 1.287.74.857 1.11 2.073 1.11 3.647 0 3.2-1.435 4.8-4.305 4.8M18.637 0L4.09 3.81.081 18.439l5.014 5.148L0 28.65l3.773 14.693 14.484 4.047 5.096-5.064 5.014 5.147 14.547-3.81 4.008-14.63-5.013-5.146 5.095-5.063L43.231 4.13 28.745.083l-5.094 5.063zM9.71 6.624l7.663-2.008 3.351 3.44-4.887 4.856zm16.822 1.478l3.405-3.383 7.63 2.132-6.227 6.187zM4.672 17.238l2.111-7.705 6.125 6.288-4.886 4.856zm29.547-1.243l6.227-6.189 1.986 7.74-3.404 3.384zm-15.502-.127l4.887-4.856 4.807 4.936-4.886 4.856zm-7.814 7.765l4.886-4.856 4.81 4.936-4.888 4.856zm15.503.127l4.886-4.856L36.1 23.84l-4.887 4.856zM4.57 29.927l3.406-3.385 4.807 4.937-6.225 6.186zm14.021 1.598l4.887-4.856 4.808 4.936-4.886 4.856zm15.502.128l4.887-4.856 3.351 3.439-2.11 7.705zm-24.656 8.97l6.226-6.189 4.81 4.936-3.406 3.385zm16.843-1.206l4.886-4.856 6.126 6.289-7.662 2.007z"></path>
                </g>
              </svg>
            </div>
            <div class="flex-auto">
              <p class="pr-4 md:pr-6 leading-5">Your information is secured by Plaid's bank level security
                standards.</p>
            </div>
          </div>
        </div>
      </template>

    </app-body>
  </app-wrapper>
</template>

<script>

import AppLogo from './../../../components/AppLogo';
import { analytics } from '../../../analytics';

export default {
  mixins: [
    require('../../../mixins/InteractsWithGtag').default,
    require('../../../mixins/PageViewAnalytics').default,
  ],
  components: {
    AppLogo
  },
  props: {
    user: {},
    loan: {},
    manual: {
      type: Boolean,
      default: false
    },
    mxWidgetUrl: {},
    plaidLinkToken: {}
  },

  data() {
    return {
      gTagPageName: 'VERIFY_INCOME',
      gEventType: 'MarketPlaceSignUp',
      showBanking: false,
      showManual: false,
      plaid: null,
      institution_id: null,
      institution_code: null,
      loading: false,
      fileName: null,
      plaidLoading: false,
      isPaystubOutdated: false,
      isPaystubInFuture: false,
      isPaystubError: false,
      isPaystubMissingName: false,
      isPaystubMissingAmount: false,
      paystubDate: '',
      expectedPayDate: '',
      parkMessage: null,
      manualForm: {
        errors: null,
        account_number: '',
        account_number_confirmation: '',
        routing_number: '',
        routing_number_confirmation: '',
        official_name: ''
      }
    }
  },

  watch: {
    showBanking() {
      setTimeout(() => {
        this.plaid = window.Plaid.create({
          token: this.plaidLinkToken,
          onSuccess: () => {
            this.plaidLoading = true;

            analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaidSuccess({
              user_id: this.user?.id ?? null,
              loan_status: this.loan?.status ?? null,
              loan_id: this.loan?.id ?? null,
              page_name: this.gTagPageName,
            });

            this.$http.post(`/signup/welcome/${this.loan.id}`, {
              income_verification_submitted: true,
            }).then(() => {
              this.plaidLoading = false;

              this.continue();
            }).catch(error => {
              this.plaidLoading = false;
            })
          },
          onEvent: (event, metadata) => {
              if (event == 'ERROR') {
                  window.Bugsnag.notify('fail IncomeVerification: ' + JSON.stringify({
                    error: metadata.error_code + ' (' + metadata.error_message + ')',
                    loan_id: this.loan?.id,
                    user_id: this.user?.id,
                  }));
              }

              this.$http?.post('/api/plaid/link-events', {
                  event_name: event,
                  loan_id: this.loan?.id ?? null,
                  institution_name: metadata.institution_name ?? metadata.institution?.name ?? null,
                  error_type: metadata.error_type ?? null,
                  error_code: metadata.error_code ?? null,
                  error_message: metadata.error_message ?? null,
                  link_session_id: metadata.link_session_id ?? null,
                  request_id: metadata.request_id ?? null,
                  exit_status: metadata.exit_status ?? null,
                  metadata: metadata,
                  event_time: new Date().toISOString(),
              }).catch(() => {});
          },
          onExit: (error, metadata) => {
            this.plaidLoading = false;

            analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaidExit({
              user_id: this.user?.id ?? null,
              loan_id: this.loan?.id ?? null,
              loan_status: this.loan?.status ?? null,
              page_name: this.gTagPageName,
              error: error,
              status: metadata.status,
              link_session_id: metadata.link_session_id,
              plaid_request_id: metadata.request_id,
            });

            window.Bugsnag.notify('fail IncomeVerification  : ' + JSON.stringify({
              error: error,
              status: metadata.status,
              link_session_id: metadata.link_session_id,
              plaid_request_id: metadata.request_id,
            }));
          },
        });

        analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaid({
            user_id: this.user?.id ?? null,
            loan_id: this.loan?.id ?? null,
            loan_status: this.loan?.status ?? null,
            page_name: this.gTagPageName,
        });

        this.plaid.open();
      }, 150);
    }
  },

  created() {
    this.getPaystubDate();
  },

  methods: {
    refresh() {
      window.location.reload();
    },

    continue() {
      this.loading = false;
      window.location.reload();
    },
    getPaystubDate() {
      this.$http.get(`/account/paystub-date`).then((response) => {
        
        if (response.data?.is_paystub_outdated || response.data?.is_paystub_in_future || response.data?.is_paystub_has_error || response.data?.is_paystub_missing_name || response.data?.is_paystub_missing_amount) {
          this.paystubDate = response.data?.paystub_date;
          this.isPaystubOutdated = response.data?.is_paystub_outdated;
          this.isPaystubInFuture = response.data?.is_paystub_in_future;
          this.isPaystubError = response.data?.is_paystub_has_error;
          this.isPaystubMissingName = response.data?.is_paystub_missing_name;
          this.isPaystubMissingAmount = response.data?.is_paystub_missing_amount;
          this.expectedPayDate = response.data?.expected_pay_date;
        }

        // Outside the flag guard on purpose: a park message exists precisely in the case every
        // flag is false — a verdict written off the bank-side gates, which no flag here covers.
        this.parkMessage = response.data?.park_message ?? null;

      })
    },
  }
}
</script>
